// Import các thư viện và hook cần thiết từ React và các thư mục khác
"use client"; // Chỉ thị này để báo đây là component phía client (chạy trên trình duyệt)

import React, { useEffect, useState } from "react"; // Import React và các hook cơ bản
import { useNavigate, useParams } from "react-router-dom"; // Hook để điều hướng và lấy tham số từ URL
import * as XLSX from "xlsx"; // Thư viện để xử lý file Excel
import { getData, postData } from "../../api/api"; // Các hàm gọi API để lấy và gửi dữ liệu
import { useStorage } from "../../hooks/useLocalStorage"; // Hook để lưu trữ dữ liệu trong localStorage
import { useNotification } from "../../Notification/Notification"; // Hook để hiển thị thông báo

export default function RetreatRegister() {
  // Khai báo các state (trạng thái) để quản lý dữ liệu trong component
  
  // State để quản lý loại đăng ký (đăng ký cho 1 người hay nhiều người)
  const [registrationType, setRegistrationType] = useState("single");
  
  // State để lưu file Excel được tải lên
  const [file, setFile] = useState(null);
  
  // State để theo dõi số lượng người tham gia
  const [participantCount, setParticipantCount] = useState(1);
  
  // State để lưu dữ liệu từ file Excel
  const [excelData, setExcelData] = useState([]);
  
  // State để lưu thông tin người dùng (sử dụng localStorage)
  const [user, setUser] = useStorage("use", {});
  
  // State để lưu thông báo lỗi
  const [error, setError] = useState("");

  // Hook để điều hướng trang
  const navigate = useNavigate();
  
  // Lấy id từ URL (ví dụ: /retreat/123)
  const { id } = useParams();
  
  // State để lưu thông tin khóa tu
  const [retreat, setRetreat] = useState({});
  
  // State để lưu giá khóa tu
  const [coursePrice, setCoursePrice] = useState(0);

  // Hook để hiển thị thông báo
  const { notificationApi } = useNotification();
  
  // State để điều khiển hiển thị modal điều khoản
  const [showTermsModal, setShowTermsModal] = useState(false);
  
  // State để theo dõi trạng thái đang gửi đăng ký
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Add new state for error messages
  const [errorMessages, setErrorMessages] = useState([]);
  const [showErrorPopup, setShowErrorPopup] = useState(false);

  // useEffect: Chạy khi component được tải lần đầu hoặc khi id thay đổi
  useEffect(() => {
    // Kiểm tra nếu chưa đăng nhập thì chuyển đến trang đăng nhập
    if (!user.id) {
      navigate("/login");
    }
    
    // Lấy thông tin khóa tu từ API
    getData(`/retreats/${id}`)
      .then((e) => {
        // Cập nhật thông tin khóa tu và giá
        setRetreat(e.data);
        setCoursePrice(e.data.cost);
      })
      .catch((error) => {
        // Ghi log nếu có lỗi
        console.log(error);
      });
  }, [id, navigate, user.id]); // Mảng phụ thuộc: useEffect sẽ chạy lại khi các giá trị này thay đổi

  // Hàm xử lý khi file Excel được chọn
  const handleFileChange = (event) => {
    // Kiểm tra xem có file được chọn không
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      if (file) {
        // Lưu file
        setFile(file);
        
        // Tạo đối tượng để đọc file
        const reader = new FileReader();
        
        // Khi file được đọc xong
        reader.onload = (e) => {
          try {
            // Chuyển đổi dữ liệu file sang dạng mảng
            const data = new Uint8Array(e.target.result);
            
            // Đọc file Excel
            const workbook = XLSX.read(data, { type: "array" });
            
            // Lấy sheet đầu tiên
            const sheet = workbook.Sheets[workbook.SheetNames[0]];

            // Chuyển sheet sang JSON, bỏ qua dòng đầu tiên (tiêu đề)
            const jsonData = XLSX.utils.sheet_to_json(sheet, {
              defval: "",
              raw: false,
              range: 1, // Bắt đầu từ dòng thứ 2
            });

            // Danh sách các cột bắt buộc
            const requiredHeaders = [
              "STT",
              "Email",
              "FirstName",
              "LastName",
              "DateOfBirth",
              "PhoneNumber",
              "Gender",
            ];
            
            // Lấy các cột trong file
            const headers = Object.keys(jsonData[0]);

            // Kiểm tra xem file có đúng các cột cần thiết không
            if (requiredHeaders.every((header) => headers.includes(header))) {
              // Nếu đúng, cập nhật số lượng người tham gia và dữ liệu
              setParticipantCount(jsonData.length);
              setExcelData(jsonData);
              setError(""); // Xóa lỗi nếu có
            } else {
              // Nếu sai, báo lỗi
              setError(
                "Excel file must contain the following columns: 'STT', 'Email', 'FirstName', 'LastName', 'DateOfBirth', 'PhoneNumber', 'Gender'."
              );
              setExcelData([]);
              setParticipantCount(0);
            }
          } catch (err) {
            // Xử lý lỗi nếu không thể đọc file
              setError(
                "An error occurred while processing the file. Please try again."
            );
            setExcelData([]);
            setParticipantCount(0);
          }
        };
        
        // Đọc file dưới dạng ArrayBuffer
        reader.readAsArrayBuffer(file);
      }
    } else {
      // Nếu không chọn file, reset các trạng thái
      setFile(null);
      setExcelData([]);
      setParticipantCount(0);
    }
  };


// Hàm xử lý khi form được submit
const handleSubmit = (event) => {
  // Ngăn chặn hành vi mặc định của form (không reload trang)
  event.preventDefault();
  // Hiển thị modal điều khoản
  setShowTermsModal(true);
};

// Hàm xử lý khi người dùng đồng ý với các điều khoản
const handleAgreeTerms = () => {
  // Ẩn modal điều khoản
  setShowTermsModal(false);
  // Bật trạng thái đang submit để disable nút và hiện loading
  setIsSubmitting(true);

  // Kiểm tra loại đăng ký là đơn lẻ
  if (registrationType === "single") {
    // Gọi API đăng ký cho 1 người
    postData("/retreat-registration/account", { retreatId: id })
      .then((response) => {
        // Sau khi đăng ký thành công, tiến hành thanh toán
        postData("/payments", { retreatRegId: response.id })
          .then((paymentResponse) => {
            // Chuyển hướng đến trang thanh toán
            window.location.href = paymentResponse.paymentUrl;
          })
          .catch((error) => {
            // Xử lý lỗi thanh toán
            console.log(error);
            
            notificationApi("error", "Payment error", "Payment error");
            // Tắt trạng thái submit để enable nút trở lại
            setIsSubmitting(false);
          });
      })
      .catch((error) => {
        // Xử lý lỗi đăng ký
        console.log(error);
        notificationApi(
          "error",
          "Registration error",
          error?.data?.message || "Registration error"
        );
        // Tắt trạng thái submit để enable nút trở lại
        setIsSubmitting(false);
      });
  } 
  // Kiểm tra loại đăng ký là nhiều người và đ�� tải file
  else if (registrationType === "multiple" && file) {
    // Gọi API tạo đăng ký cho nhiều người
    postData(`/retreat-registration`, {
      createBy: user.email, // Email người tạo
      retreatId: id, // ID khóa tu
      totalCost: coursePrice * participantCount, // Tổng chi phí
      totalParticipants: participantCount, // Tổng số người tham gia
      isDeleted: false, // Trạng thái không bị xóa
      isPaid: false, // Trạng thái chưa thanh toán
    })
    .then((e) => {
      // Tạo FormData để upload file
      const formData = new FormData();
      formData.append("file", file); // Thêm file Excel
      formData.append("retreatRegId", e.id); // Thêm ID đăng ký

      // Gọi API upload danh sách người tham gia
      postData(`/retreat-participant`, formData)
        .then((registration) => {
          // Tiến hành thanh toán
          postData(`/payments`, { retreatRegId: registration?.id })
            .then((e) => {
              // Chuyển hướng đến trang thanh toán
              window.location.href = e.paymentUrl;
            })
            .catch((error) => {
              // Xử lý lỗi thanh toán
              console.log(error);
              notificationApi("error", "Payment error", "Payment error");
              setIsSubmitting(false);
            });
        })
        .catch((error) => {
          // Only show notification if there's a message
          if (error?.data?.message) {
            notificationApi(
              "error",
              "File error",
              error.data.message
            );
          }
          
          // Check for error messages array
          if (error?.data?.messages) {
            setErrorMessages(error.data.messages);
            setShowErrorPopup(true);
          }
          
          console.log(error);
          setIsSubmitting(false);
        });
    })
    .catch((error) => {
      // Check for error messages
      if (error?.data?.messages) {
        setErrorMessages(error.data.messages);
        setShowErrorPopup(true);
      }
      setIsSubmitting(false);
    });
  } 
  // Trường hợp đăng ký nhiều người nhưng không tải file
  else {
    // Hiển thị thông báo lỗi
    notificationApi(
      "error",
      "No file uploaded",
      "Please upload a file for multiple registration."
    );
    // Tắt trạng thái submit
    setIsSubmitting(false);
  }
};

// Hàm xử lý khi người dùng hủy điều khoản
const handleCancelTerms = () => {
  // Ẩn modal điều khoản
  setShowTermsModal(false);
};

// Hàm tính tổng chi phí
const calculateTotalPrice = () => {
  // Tổng chi phí = giá khóa tu * số lượng người tham gia
  return coursePrice * participantCount;
};

  return (
    <div className="flex flex-col md:flex-row bg-gray-100 min-h-screen">
      {/* Left column: Course Information */}
      <div className="md:w-3/5 flex p-4 flex-col items-center">
        <div className="bg-white rounded-lg shadow-lg p-4 w-full">
          <img
            src={retreat.retreatImages && retreat.retreatImages[0]?.url}
            alt="Course"
            className="rounded-md mb-6 w-full"
          />
          <h2 className="text-3xl font-bold mb-4 text-center">
            {retreat.name}
          </h2>
          <p className="text-gray-600 mb-4 text-center">
            Detailed information about the course
          </p>
          <div
            className="uploaded-content"
            dangerouslySetInnerHTML={{ __html: retreat.description }}
          />
        </div>
      </div>

      {/* Right column: Registration Form */}
      <div className="md:w-2/5 p-4 flex flex-col items-center">
        <div className="bg-white rounded-lg shadow-lg p-4 w-full">
          <h2 className="text-3xl font-bold mb-4 text-center">
            Course Registration
          </h2>
          <p className="text-gray-600 mb-4 text-center">
            Choose registration type and payment
          </p>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex space-x-4 justify-center">
              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  value="single"
                  checked={registrationType === "single"}
                  onChange={() => {
                    setRegistrationType("single");
                    setParticipantCount(1);
                    setExcelData([]);
                    setFile(null); // Reset file when switching to single registration
                  }}
                  className="form-radio"
                />
                <span>Register for 1 person</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  value="multiple"
                  checked={registrationType === "multiple"}
                  onChange={() => setRegistrationType("multiple")}
                  className="form-radio"
                />
                <span>Register for multiple people</span>
              </label>
            </div>

            {registrationType === "multiple" && (
              <div className="space-y-2">
                <label
                  htmlFor="file"
                  className="block text-sm font-medium text-gray-700"
                >
                  Upload list (Excel)
                </label>
                <p>
                  Excel template:{" "}
                  <a
                    rel="noopener noreferrer"
                    target="_blank"
                    className="text-blue-500"
                    href="https://drive.google.com/drive/folders/19yDm9aNGQM1MJf5rHqzTli8WOmYmX6aj?usp=sharing"
                  >
                    Link
                  </a>
                </p>
                <input
                  id="file"
                  type="file"
                  accept=".xlsx, .xls"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-500
                            file:mr-4 file:py-2 file:px-4
                            file:rounded-full file:border-0
                            file:text-sm file:font-semibold
                            file:bg-yellow-50 file:text-yellow-700
                            hover:file:bg-yellow-100"
                />
              </div>
            )}

            {error && (
              <p className="text-red-500 font-semibold mb-4 text-center">
                {error}
              </p>
            )}

            <div className="space-y-2 text-center">
              <p className="text-sm text-gray-500">
                Price per person: ${coursePrice.toLocaleString()}
              </p>
              <p className="text-sm text-gray-500">
                Number of participants: {participantCount}
              </p>
              <p className="text-lg font-bold text-yellow-700">
                Total cost: ${calculateTotalPrice().toLocaleString()}
              </p>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full ${
                isSubmitting
                  ? "bg-gray-400"
                  : "bg-yellow-500 hover:bg-yellow-600"
              } text-white rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-opacity-50`}
            >
              {isSubmitting ? "Registering..." : "Proceed to payment"}
            </button>
          </form>

          {excelData.length > 0 && (
            <div className="mt-4">
              <h3 className="text-lg font-semibold text-gray-700 mb-2 text-center">
                Registration list from Excel:
              </h3>
              <table className="w-full max-w-lg border border-collapse mt-4">
                <thead>
                  <tr>
                    <th className="bg-green-600 text-white px-4 py-2 text-left font-medium">
                      STT
                    </th>
                    <th className="bg-green-600 text-white px-4 py-2 text-left font-medium">
                      Email
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {excelData.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className={
                        rowIndex % 2 === 0 ? "bg-gray-100" : "bg-white"
                      }
                    >
                      <td className="border px-4 py-2 text-gray-700">
                        {row["STT"]}
                      </td>
                      <td className="border px-4 py-2 text-gray-700">
                        {row["Email"]}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {showTermsModal && (
            <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50">
              <div className="bg-white rounded-lg shadow-lg p-6 w-11/12 md:w-1/2">
                <h2 className="text-2xl font-bold mb-4">
                  Terms and Conditions
                </h2>
                <div className="h-64 overflow-y-scroll mb-4">
                  {/* Replace this with the actual terms and conditions */}
                  <p>
                    <strong>Refund Policy:</strong>
                  </p>
                  <p>
                    A refund of 80% of the fee is provided if you cancel.
                  </p>
                  <p>
                    <strong>Payment:</strong>
                  </p>
                  <p>
                    Full payment is required upon registration. Payment must be made via PayPal.If the user has not paid, the order will be in a pending state.If the order is in a pending state, please go to the transaction history to proceed with the payment. If the order remains pending for 1 day, the bill will be automatically canceled
                  </p>
                  <p>
                    <strong>Slot Availability:</strong>
                  </p>
                  <p>
                    If the minimum number of participants (slots) is not reached by the start date, Plum Village is not responsible for any refunds or compensation.
                  </p>
                  <p>
                    Please read the following terms and conditions carefully before proceeding with your registration. By clicking "Agree", you acknowledge that you have read, understood, and agree to be bound by these terms.
                  </p>

                </div>
                <div className="flex justify-end space-x-4">
                  <button
                    onClick={handleCancelTerms}
                    disabled={isSubmitting}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAgreeTerms}
                    disabled={isSubmitting}
                    className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded"
                  >
                    Agree
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Error Messages Popup */}
      {showErrorPopup && errorMessages.length > 0 && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="fixed inset-0 bg-black opacity-50"></div>
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 relative z-50">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-red-600">Error Messages in Excel File</h3>
              <button
                onClick={() => setShowErrorPopup(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="max-h-60 overflow-y-auto">
              <ul className="space-y-2">
                {errorMessages.map((message, index) => (
                  <li key={index} className="text-red-500 flex items-start">
                    <span className="mr-2">•</span>
                    <span>{message}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowErrorPopup(false)}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}